import { LitElement, html, css, unsafeCSS } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import styles from "./input-modal.css?inline";
import { sendQuestion } from "../../services/chatbot.service";
import snarkdown from "../../libs/snarkdown";
import { iconChevronDown, iconSend } from "../../icons";
import { CHAT_BOT_SRC } from "../../constants/urls.constant";
import { LocalStorage } from "../../constants/local-storage.constant";
import {
  isBrowser,
  getWindow,
  getLocalStorage,
  runInBrowser,
} from "../../utils/dom-utils";
import { CURRENT_VERSION } from "../../constants/configurations";

@customElement("input-modal")
export class InputModal extends LitElement {
  @property({ type: String, attribute: "unique-organization-token" })
  uniqueOrganizationToken = "";
  @property({ type: String }) placeholder = "¿En qué podemos ayudarte?";
  @property({ type: String, attribute: "primary-color" }) primaryColor =
    "#0066FF";
  @property({ type: String, attribute: "secondary-color" }) secondaryColor =
    "#00FFC6";
  @property({ type: String }) title = "Asistente Virtual";
  @property({ type: String, attribute: "img" }) avatarSrc = CHAT_BOT_SRC;
  @property({ type: String, attribute: "navbar-height" }) navbarHeight = "0px";
  @property({ type: String, attribute: "welcome-message" }) welcomeMessage =
    "¡Hola! Soy tu asistente";
  @property({ type: String, attribute: "message" }) message =
    "¿En qué puedo ayudarte?";
  @property({ type: String, attribute: "text-loading" }) textLoading =
    "Escribiendo";
  @property({ type: String, attribute: "dark-mode" }) darkMode = "true";
  @property({ type: String, attribute: "text-color" }) textColor = "white";
  @property({ type: String, attribute: "text-input-out" }) textInputOut =
    "white";

  @state() private modalOpen = false;
  @state() private modalPosition: DOMRect | null = null;
  @state() private inputValue = "";
  @state() private messages: Array<{
    from: "user" | "bot" | "error";
    text: string;
  }> = [];
  @state() private isLoading: Boolean = false;

  @query("#mainInput") mainInput!: HTMLInputElement;
  @query(".messages") messageContainer!: HTMLDivElement;

  connectedCallback() {
    super.connectedCallback();
    runInBrowser(() => {
      this.loadMessagesFromStorage();
      const window = getWindow();
      if (window) {
        window.addEventListener("keydown", this._onKeyDown);
      }
    });
  }

  disconnectedCallback() {
    runInBrowser(() => {
      const window = getWindow();
      if (window) {
        window.removeEventListener("keydown", this._onKeyDown);
      }
    });
    super.disconnectedCallback();
  }

  private closeModal() {
    this.modalOpen = false;
    this.inputValue = "";
  }

  private _handleOverlayClick() {
    this.closeModal();
  }

  private _onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape") this.closeModal();
  };

  private getInputPosition() {
    if (!isBrowser) return null;

    const input = this.mainInput;
    if (!input) return null;
    return input.getBoundingClientRect();
  }

  private saveMessagesToStorage() {
    if (!isBrowser) return;

    try {
      const storage = getLocalStorage();
      if (storage) {
        storage.setItem(
          LocalStorage.INPUT_MODAL_STORAGE_KEY,
          JSON.stringify(this.messages)
        );
      }
    } catch (e) {
      console.warn("No se pudo guardar el historial del chat modal", e);
    }
  }

  private loadMessagesFromStorage() {
    if (!isBrowser) return;

    try {
      const storage = getLocalStorage();
      if (storage) {
        const stored = storage.getItem(LocalStorage.INPUT_MODAL_STORAGE_KEY);
        if (stored) {
          const parsed = JSON.parse(stored);
          if (Array.isArray(parsed)) {
            this.messages = parsed;
          }
        }
      }
    } catch (e) {
      console.warn("No se pudo cargar el historial del chat modal", e);
    }
  }

  private openModal() {
    if (!isBrowser) return;

    const position = this.getInputPosition();
    if (!position) return;
    this.modalPosition = position;
    this.modalOpen = true;

    if (this.inputValue.trim()) {
      this.messages = [
        ...this.messages,
        { from: "user", text: this.inputValue.trim() },
      ];
      this.inputValue = "";
      setTimeout(() => {
        this.addBotResponse("Hola, soy tu asistente virtual.");
      }, 600);
    }
  }

  private goToEndMessage() {
    if (!isBrowser) return;

    if (this.messageContainer) {
      this.updateComplete.then(() => {
        this.messageContainer.scrollTop = this.messageContainer?.scrollHeight;
      });
    }
  }

  private async sendMessage() {
    if (!this.inputValue.trim()) return;

    const userMessage = this.inputValue.trim();
    this.messages = [...this.messages, { from: "user", text: userMessage }];

    this.inputValue = "";
    this.isLoading = true;

    let botPartial = "";

    await sendQuestion(
      {
        message: userMessage,
        uniqueOrganizationToken: this.uniqueOrganizationToken,
      },
      (chunk, success) => {
        if (!success) {
          this.messages = [...this.messages, { from: "error", text: chunk }];
          this.isLoading = false;
          return;
        }

        botPartial += chunk;

        const lastMessage = this.messages[this.messages.length - 1];
        if (lastMessage?.from === "bot") {
          this.messages = [
            ...this.messages.slice(0, this.messages.length - 1),
            { from: "bot", text: botPartial },
          ];
        } else {
          this.messages = [...this.messages, { from: "bot", text: botPartial }];
        }

        this.goToEndMessage();
      }
    );

    this.isLoading = false;
  }

  private addBotResponse(text: string) {
    this.messages = [...this.messages, { from: "bot", text }];
  }

  static styles = css`
    ${unsafeCSS(styles)}
  `;

  willUpdate(changedProps: Map<string, unknown>) {
    if (changedProps.has("messages")) {
      this.saveMessagesToStorage();
    }
  }

  updated() {
    if (this.modalOpen) {
      requestAnimationFrame(() => {
        const modal = this.renderRoot.querySelector(".modal");
        const chatInput = this.renderRoot.querySelector("#chat-input");
        if (modal) modal.classList.add("fullscreen");
        if (chatInput) (chatInput as HTMLInputElement).focus();
      });
    }

    this.goToEndMessage();
    this.style.setProperty("--primary-color", this.primaryColor);
    this.style.setProperty("--secondary-color", this.secondaryColor);
    this.style.setProperty("--navbar-height", this.navbarHeight);
    this.style.setProperty("--text-color", this.textColor);
    this.style.setProperty("--text-input-out", this.textInputOut);

    const darkMode = this.darkMode == "true" ? true : false;
    if (darkMode) {
      this.style.setProperty("--background-modal", "#000000aa");
    } else {
      this.style.setProperty("--background-modal", "#ffffff22");
    }
  }

  render() {
    return html`
      <label
        for="mainInput"
        class="chat-input-wrapper ${this.modalOpen && "opened"}"
        @click=${this.openModal}
      >
        <img
          src=${this.avatarSrc}
          alt="Chatbot avatar"
          aria-label-hidden="true"
          class="chat-input-img"
        />
        <input
          class="chat-text-input out"
          id="mainInput"
          autocomplete="off"
          type="text"
          placeholder=${this.placeholder}
          .value=${this.inputValue}
          @input=${(e: any) => (this.inputValue = e.target.value)}
          @keydown=${(e: KeyboardEvent) =>
            e.key === "Enter" && this.openModal()}
        />
        <button class="chat-button" @click=${this.openModal}>
          ${iconSend}
        </button>
      </label>

      ${this.modalOpen && this.modalPosition
        ? html`
            <div class="overlay" @click=${this._handleOverlayClick}>
              <div
                class="modal"
                style="
                    top: ${this.modalPosition.top}px;
                    left: ${this.modalPosition.left}px;
                    width: ${this.modalPosition.width}px;
                    height: ${this.modalPosition.height}px;
                "
                @click=${(e: Event) => e.stopPropagation()}
              >
                <div class="modal-background">
                  <div class="modal-content">
                    <div>
                      <button class="close-button" @click=${this.closeModal}>
                        ${iconChevronDown}
                      </button>
                    </div>
                    <div
                      class="empty-chat ${this.messages.length > 0 && "hidden"}"
                    >
                      <div class="empty-messages">
                        <div>
                          <p>${this.welcomeMessage}</p>
                          <span>${this.message}</span>
                        </div>
                        <img
                          src=${this.avatarSrc}
                          alt="Chatbot avatar"
                          aria-label-hidden="true"
                        />
                      </div>
                    </div>
                    <div class="messages">
                      ${this.messages.map((msg) =>
                        msg.from === "bot"
                          ? html`
                              <div class="message-wrapper bot">
                                <img
                                  src=${this.avatarSrc}
                                  alt="Bot avatar"
                                  class="avatar"
                                />
                                <div
                                  class="message bot"
                                  .innerHTML=${snarkdown(msg.text)}
                                ></div>
                              </div>
                            `
                          : msg.from == "error"
                          ? html`
                              <div class="message-wrapper error">
                                <img
                                  src=${this.avatarSrc}
                                  alt="Bot avatar"
                                  class="avatar"
                                />
                                <div
                                  class="message bot"
                                  .innerHTML=${snarkdown(msg.text)}
                                ></div>
                              </div>
                            `
                          : html`
                              <div class="message-wrapper user">
                                <div class="message user">${msg.text}</div>
                              </div>
                            `
                      )}
                      ${this.isLoading
                        ? html`
                            <div class="message-wrapper">
                              <img
                                class="bot-avatar-loading"
                                src=${this.avatarSrc}
                                alt="Bot avatar"
                              />
                              <div class="typing-message">
                                ${this.textLoading}
                              </div>
                            </div>
                          `
                        : null}
                    </div>

                    <div>
                      <label for="chat-input" class="chat-input-wrapper">
                        <img
                          src=${this.avatarSrc}
                          alt="Chatbot avatar"
                          aria-label-hidden="true"
                          class="chat-input-img"
                        />
                        <input
                          id="chat-input"
                          class="chat-text-input"
                          type="text"
                          autocomplete="off"
                          placeholder="${this.placeholder}"
                          .value=${this.inputValue}
                          ?disabled=${this.isLoading}
                          @input=${(e: any) =>
                            (this.inputValue = e.target.value)}
                          @keydown=${(e: KeyboardEvent) =>
                            e.key === "Enter" && this.sendMessage()}
                        />
                        <button
                          class="chat-button"
                          ?disabled=${this.isLoading}
                          @click=${this.sendMessage}
                        >
                          ${this.isLoading
                            ? html` <div class="loader"></div> `
                            : iconSend}
                        </button>
                      </label>
                      <p class="credits">
                        Powered by <strong>GlobalsOne</strong> -
                        v${CURRENT_VERSION}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          `
        : null}
    `;
  }
}
